/**
 * Recipe library — 8 launch articles.
 * Structured to feed Google's Recipe rich-result cards via schema.org/Recipe
 * JSON-LD (see /components/Seo.jsx). Each recipe cross-links to the product
 * SKU it stars so /recipes doubles as a shop-conversion funnel.
 *
 * Ingredient/instruction copy is deliberately Aussie-English and speaks to
 * home cooks first (Google's Recipe eligibility rewards natural language +
 * step imagery + author). Keep this file the single source of truth — no
 * markdown files, no CMS, just JavaScript objects the pages import.
 */

const R = (slug, data) => ({ slug, ...data });

export const RECIPES = [
  R('wagyu-beef-cheek-ragu', {
    title: 'Wagyu Beef Cheek Ragu',
    tagline: 'Six hours of slow braise, twelve minutes to serve.',
    description: 'Wagyu beef cheeks braised in red wine and San Marzano tomatoes with soffritto, garlic and rosemary. Falls apart with a spoon; loves fresh pappardelle. A cold-Sunday centrepiece that punches well above its price point.',
    cuisine: 'Italian',
    category: 'Main course',
    difficulty: 'Easy',
    yield: '6 servings',
    prepMinutes: 25,
    cookMinutes: 355,
    keywords: 'beef cheek ragu, wagyu recipe, slow-cooked beef, pappardelle ragu, Sunday roast Perth, halal beef Italian',
    heroImage: '/hero/recipes/wagyu-beef-cheek-ragu-1024.webp',
    productSlug: 'beef-diced-beef',
    pairingSlugs: ['beef-osso-bucco', 'beef-brisket'],
    ingredients: [
      '1.5 kg Wagyu beef cheeks, silver skin trimmed',
      '2 medium brown onions, finely diced',
      '2 carrots, finely diced',
      '2 celery stalks, finely diced',
      '6 garlic cloves, crushed',
      '2 tbsp tomato paste',
      '400 ml full-bodied red wine (Shiraz, Cabernet, or Barolo)',
      '800 g crushed San Marzano tomatoes',
      '500 ml beef stock',
      '2 sprigs fresh rosemary',
      '2 fresh bay leaves',
      '3 tbsp olive oil',
      'Sea salt & cracked black pepper',
      '500 g fresh pappardelle, to serve',
      'Grated pecorino & torn basil, to finish',
    ],
    instructions: [
      { name: 'Season & sear', text: 'Season the cheeks generously with salt and pepper. Heat 2 tbsp olive oil in a heavy Dutch oven over high heat and sear cheeks in batches until deeply caramelised on all sides — 3-4 minutes per side. Set aside.' },
      { name: 'Build the soffritto', text: 'Reduce heat to medium. Add remaining oil, onion, carrot and celery. Sweat gently for 10 minutes until softened and sweet. Add garlic and cook for 1 minute more.' },
      { name: 'Deglaze', text: 'Stir in tomato paste and cook for 2 minutes. Pour in red wine, scrape the base, and simmer until almost fully reduced — about 5 minutes.' },
      { name: 'Braise low & slow', text: 'Return the cheeks to the pot with any resting juices. Add crushed tomatoes, stock, rosemary and bay. Bring to a simmer, cover with a lid, and slide into a 150 °C oven for 5 – 5½ hours, until the meat surrenders to a fork.' },
      { name: 'Shred & finish', text: 'Lift the cheeks out, shred with two forks, discard any large fat pieces, and return to the sauce. Simmer uncovered for 15 minutes to thicken. Season to taste.' },
      { name: 'Serve', text: 'Cook pappardelle to al dente, toss through the ragu with a splash of pasta water, and finish with grated pecorino and torn basil. Serve immediately.' },
    ],
    tips: [
      'Wagyu cheeks are richer than standard beef cheek — resist the urge to add butter to the sauce.',
      'The ragu freezes beautifully for up to 3 months. Portion into 500 g bags for a mid-week rescue meal.',
    ],
    dateISO: '2026-02-14',
  }),

  R('halal-lamb-shoulder-slow-roast', {
    title: 'Halal Lamb Shoulder Slow-Roast with Middle-Eastern Spices',
    tagline: 'Seven-hour lamb that pulls apart with a fork.',
    description: 'Whole Halal-certified WA lamb shoulder slow-roasted with sumac, cumin, garlic and Aleppo pepper. Rendered fat, crackling edges, spoon-tender meat. Serve with warm flatbreads, herbed labneh and a heap of pomegranate for a table centrepiece that feeds twelve.',
    cuisine: 'Middle Eastern',
    category: 'Main course',
    difficulty: 'Easy',
    yield: '8 – 10 servings',
    prepMinutes: 20,
    cookMinutes: 420,
    keywords: 'halal lamb shoulder recipe, slow roast lamb, Middle Eastern lamb, halal lamb Perth, pulled lamb wraps, lamb shoulder sumac',
    heroImage: '/hero/recipes/halal-lamb-shoulder-slow-roast-1024.webp',
    productSlug: 'lamb-square-cut-shoulder',
    pairingSlugs: ['lamb-leg-roast', 'lamb-shanks'],
    ingredients: [
      '2.5 kg Halal WA lamb shoulder, bone-in',
      '6 garlic cloves, sliced',
      '2 tbsp sumac',
      '2 tbsp ground cumin',
      '1 tbsp Aleppo pepper (or 1 tsp chilli flakes)',
      '2 tsp ground cinnamon',
      '2 tsp sea salt flakes',
      '¼ cup olive oil',
      '2 lemons, zest + juice',
      '2 brown onions, thickly sliced',
      '500 ml chicken or vegetable stock',
      'Warm flatbreads, labneh, pomegranate seeds and mint, to serve',
    ],
    instructions: [
      { name: 'Score & marinate', text: 'Score the fat side of the lamb in a diamond pattern, taking care not to cut into the meat. Poke garlic slivers into each score. In a bowl, mix sumac, cumin, Aleppo, cinnamon, salt, olive oil and lemon zest & juice into a paste. Rub over the entire shoulder. Rest at room temp for 30 minutes (or refrigerate up to 24 hours).' },
      { name: 'Set the bed', text: 'Preheat oven to 160 °C fan. Scatter sliced onions on the base of a heavy roasting tin. Sit the lamb on top and pour stock around (not over) the meat.' },
      { name: 'Cover & braise', text: 'Cover tightly with foil, sealing all sides. Roast for 5 hours undisturbed.' },
      { name: 'Crackle', text: 'Remove foil, spoon over the pan juices, and roast uncovered for another 60 – 90 minutes at 190 °C fan until the top is deeply caramelised and the meat pulls easily from the bone.' },
      { name: 'Rest & serve', text: 'Rest 20 minutes, then shred with two forks. Spoon the pan juices back over. Serve with warm flatbreads, cool labneh, pomegranate seeds and torn mint.' },
    ],
    tips: [
      'The pan juices are the treasure — strain them, skim excess fat, and drizzle over each serving.',
      'Leftovers make phenomenal ragu, wraps or shepherd\'s pie for two days after.',
    ],
    dateISO: '2026-02-12',
  }),

  R('chuck-tender-rendang', {
    title: 'Chuck Tender Beef Rendang',
    tagline: 'Dry, dark and impossibly aromatic — the Malaysian classic.',
    description: 'Slow-cooked chuck tender in coconut milk, lemongrass, galangal and toasted kerisik until every drop of moisture has caramelised into a dark, sticky spice paste. Halal-certified beef, patient technique. Serve with coconut rice and pickled cucumber.',
    cuisine: 'Malaysian',
    category: 'Main course',
    difficulty: 'Medium',
    yield: '6 servings',
    prepMinutes: 30,
    cookMinutes: 210,
    keywords: 'beef rendang recipe, halal rendang, Malaysian beef curry, chuck tender rendang, kerisik rendang, halal Australian beef Malaysia',
    heroImage: '/hero/recipes/chuck-tender-rendang-1024.webp',
    productSlug: 'beef-chuck-tender',
    pairingSlugs: ['beef-brisket', 'beef-diced-beef'],
    ingredients: [
      '1.2 kg chuck tender, cut into 4 cm cubes',
      '2 x 400 ml coconut milk',
      '½ cup toasted desiccated coconut, ground to kerisik',
      '3 kaffir lime leaves',
      '2 lemongrass stalks, bruised',
      '1 cinnamon stick',
      '3 star anise',
      '2 tbsp gula melaka (palm sugar)',
      '2 tbsp tamarind concentrate',
      'Spice paste:',
      '10 dried chillies, soaked & drained',
      '6 shallots',
      '4 garlic cloves',
      '4 cm galangal',
      '4 cm fresh turmeric',
      '2 lemongrass stalks (white part only)',
      '2 tbsp neutral oil',
    ],
    instructions: [
      { name: 'Blend the paste', text: 'Blitz all spice-paste ingredients to a smooth paste, adding a splash of water if needed.' },
      { name: 'Fry the paste', text: 'Heat a heavy-based pot over medium. Add the paste and fry for 8 – 10 minutes, stirring constantly, until deeply fragrant and the oil separates.' },
      { name: 'Combine', text: 'Add beef and stir to coat. Pour in coconut milk, and add kaffir lime leaves, lemongrass, cinnamon and star anise. Bring to a gentle simmer.' },
      { name: 'Slow-reduce', text: 'Simmer uncovered, stirring often, for 2 – 2½ hours. The liquid will slowly darken from cream to caramel to deep mahogany.' },
      { name: 'Finish with kerisik', text: 'When the sauce is very thick, stir in kerisik (ground toasted coconut), gula melaka and tamarind. Cook for 15 more minutes, stirring, until the beef is dark, glossy and the paste hugs each piece.' },
      { name: 'Rest', text: 'Rest for 30 minutes. Rendang tastes even better the next day.' },
    ],
    tips: [
      'Chuck tender holds shape better than chuck steak — it stays as beautiful chunks rather than shredding.',
      'Rendang is a "dry" curry — resist adding more coconut milk if it looks stiff. That density is the point.',
    ],
    dateISO: '2026-02-10',
  }),

  R('tomahawk-salsa-verde', {
    title: 'Grilled Tomahawk with Salsa Verde',
    tagline: 'The show-stopper. Twelve minutes of cook, sixty seconds of reveal.',
    description: 'A 1.2 kg dry-aged tomahawk, reverse-seared for a bruised-plum crust and edge-to-edge pink centre, cut in front of your guests and finished with a bright caper-and-parsley salsa verde. Feeds three generously — or two chef-friends comfortably.',
    cuisine: 'Steakhouse',
    category: 'Main course',
    difficulty: 'Medium',
    yield: '3 servings',
    prepMinutes: 15,
    cookMinutes: 50,
    keywords: 'tomahawk steak recipe, reverse sear tomahawk, dry aged beef Perth, salsa verde steak, wagyu tomahawk, steak dinner party',
    heroImage: '/hero/recipes/tomahawk-salsa-verde-1024.webp',
    productSlug: 'beef-tomahawk-steak',
    pairingSlugs: ['beef-rib-eye-steak', 'beef-scotch-fillet-steak'],
    ingredients: [
      '1 x 1.2 kg tomahawk steak, dry-aged 21+ days',
      'Sea salt flakes',
      'Cracked black pepper',
      '2 tbsp neutral oil',
      '50 g unsalted butter',
      '4 sprigs thyme',
      '3 garlic cloves, skin on, bruised',
      'Salsa verde:',
      '1 large bunch flat-leaf parsley',
      '2 tbsp baby capers, drained',
      '4 anchovy fillets (optional — skip for Halal-only tables)',
      '2 garlic cloves',
      '1 tsp Dijon mustard',
      '2 tbsp red wine vinegar',
      '150 ml extra-virgin olive oil',
    ],
    instructions: [
      { name: 'Temper', text: 'Take the steak out of the fridge 90 minutes before cooking. Pat dry, season the flesh (not the bone) very generously with salt.' },
      { name: 'Reverse-sear', text: 'Set the oven to 100 °C fan with a wire rack on a tray. Roast the steak until the internal temperature reads 46 °C for rare or 52 °C for medium-rare — about 40 minutes for a 1.2 kg. Rest 10 minutes.' },
      { name: 'Blitz the salsa verde', text: 'Pulse parsley, capers, anchovies (if using), garlic, mustard and vinegar in a food processor until finely chopped. Stream in olive oil. Taste and adjust acid.' },
      { name: 'Sear hard', text: 'Heat a cast-iron pan very hot. Add oil, then the steak. Sear 90 seconds per side, standing it on the fat cap for a further 60 seconds to render. Toss in butter, thyme and garlic. Baste for the last 30 seconds.' },
      { name: 'Rest, slice, serve', text: 'Rest 8 minutes. Carve off the bone, slice against the grain, arrange on a warmed board. Spoon salsa verde over the top and cracked salt on the fat cap.' },
    ],
    tips: [
      'A digital probe thermometer is non-negotiable for tomahawks — you cannot judge doneness by feel alone.',
      'Save the bone — it makes an incredible beef stock the next day.',
    ],
    dateISO: '2026-02-08',
  }),

  R('beef-osso-bucco-milanese', {
    title: 'Beef Osso Bucco Milanese',
    tagline: 'Marrow, gremolata, saffron risotto. Winter\'s best case.',
    description: 'Beef shin braised on the bone in white wine and stock until the marrow melts and the meat drops off the fork. Served with saffron risotto Milanese and a sharp lemon-parsley gremolata that cuts through the richness. A classic worth the four hours it asks for.',
    cuisine: 'Italian',
    category: 'Main course',
    difficulty: 'Medium',
    yield: '4 servings',
    prepMinutes: 25,
    cookMinutes: 220,
    keywords: 'beef osso bucco recipe, osso bucco Milanese, beef shin recipe, gremolata risotto, halal osso bucco, winter braise Perth',
    heroImage: '/hero/recipes/beef-osso-bucco-milanese-1024.webp',
    productSlug: 'beef-osso-bucco',
    pairingSlugs: ['beef-gravy-beef', 'beef-oxtail'],
    ingredients: [
      '4 x thick-cut beef osso bucco (250 – 300 g each)',
      'Plain flour, seasoned, for dusting',
      '3 tbsp olive oil',
      '2 medium onions, finely diced',
      '2 carrots, finely diced',
      '2 celery stalks, finely diced',
      '4 garlic cloves, sliced',
      '250 ml dry white wine',
      '400 g tinned tomatoes',
      '500 ml beef or chicken stock',
      '2 bay leaves',
      'Sea salt, black pepper',
      'Gremolata:',
      'Zest of 1 lemon',
      '½ bunch flat-leaf parsley, finely chopped',
      '2 garlic cloves, finely grated',
      'Saffron risotto Milanese, to serve',
    ],
    instructions: [
      { name: 'Sear', text: 'Tie a length of kitchen string around each osso bucco to hold the meat on the bone. Dust in seasoned flour. Sear in olive oil, 3 minutes per side, until deeply browned. Set aside.' },
      { name: 'Soffritto', text: 'Sauté onion, carrot, celery and garlic in the same pot for 10 minutes until soft.' },
      { name: 'Deglaze', text: 'Add wine, scrape the base, and reduce by two-thirds. Add tomatoes, stock and bay leaves.' },
      { name: 'Braise', text: 'Return the shin to the pot, spooning over the sauce. Cover and slide into a 150 °C oven for 3 – 3½ hours, turning once, until the meat is tender and the marrow is glossy.' },
      { name: 'Finish', text: 'Chop the gremolata ingredients together. Serve each osso bucco over saffron risotto Milanese with a generous scattering of gremolata.' },
    ],
    tips: [
      'The marrow is the prize — provide a marrow spoon or long-handled teaspoon to your guests.',
      'For a Halal table, use a Halal-certified white wine or replace with additional stock and 1 tbsp of white wine vinegar.',
    ],
    dateISO: '2026-02-05',
  }),

  R('chargrilled-chicken-skewers', {
    title: 'Chargrilled Halal Chicken Skewers with Toum',
    tagline: 'Perth summer on a plate. Marinade in the morning, grill at sunset.',
    description: 'Free-range Halal chicken thigh, cubed, marinated in yoghurt, garlic and warm spices, then chargrilled hot and fast until charred at the edges and juicy through the middle. Served with garlicky Lebanese toum, warm pita and a fistful of parsley.',
    cuisine: 'Lebanese',
    category: 'Main course',
    difficulty: 'Easy',
    yield: '6 servings',
    prepMinutes: 30,
    cookMinutes: 12,
    keywords: 'chicken skewer recipe, halal chicken kebab, shish taouk, Lebanese chicken, toum recipe, Perth BBQ, halal chicken Perth',
    heroImage: '/hero/recipes/chargrilled-halal-chicken-skewers-1024.webp',
    productSlug: 'poultry-chicken-thigh-fillets',
    pairingSlugs: ['poultry-chicken-breast-fillets', 'poultry-chicken-tenderloins'],
    ingredients: [
      '1.2 kg boneless free-range Halal chicken thigh, cut into 3 cm cubes',
      'Marinade:',
      '250 g plain Greek yoghurt',
      '4 garlic cloves, crushed',
      '2 tbsp lemon juice',
      '2 tbsp olive oil',
      '1 tsp ground allspice',
      '1 tsp smoked paprika',
      '1 tsp ground cumin',
      '½ tsp ground cinnamon',
      '2 tsp sea salt',
      'Skewers, pita, chopped tomato & pickled turnip to serve',
      'Toum (Lebanese garlic sauce), to serve',
    ],
    instructions: [
      { name: 'Marinate', text: 'Whisk marinade ingredients in a large bowl. Add chicken and toss to coat every piece. Refrigerate at least 4 hours, ideally 12 – 24.' },
      { name: 'Skewer', text: 'Thread 4 – 5 pieces per skewer, packing tightly for even cooking. Let sit at room temp for 15 minutes before grilling.' },
      { name: 'Grill', text: 'Heat a chargrill or BBQ to searing hot. Grill skewers 4 – 5 minutes per side, turning once, until charred at the edges and just cooked through (internal 74 °C).' },
      { name: 'Rest', text: 'Rest 3 minutes tented in foil. Serve on warm pita with toum, tomato, pickled turnip and parsley.' },
    ],
    tips: [
      'The yoghurt-and-lemon marinade tenderises the meat as it flavours it — don\'t skip the fridge time.',
      'Bamboo skewers? Soak for 30 minutes first, or they\'ll ignite on a hot grill.',
    ],
    dateISO: '2026-02-02',
  }),

  R('pastrami-on-rye', {
    title: 'Pastrami on Rye — The New York Deli Classic',
    tagline: 'Toasted rye, warm pastrami, yellow mustard, pickle. Nothing else.',
    description: 'A properly stacked, warm pastrami sandwich made with slow-cured, cracked-pepper Halal pastrami. Steamed to spoon-tender, layered thick, and dressed only with sharp yellow mustard and a crisp dill pickle. Simple food, done well.',
    cuisine: 'American',
    category: 'Sandwich',
    difficulty: 'Easy',
    yield: '2 sandwiches',
    prepMinutes: 5,
    cookMinutes: 15,
    keywords: 'pastrami on rye recipe, halal pastrami, deli sandwich, hot pastrami sandwich, house-cured pastrami Perth',
    heroImage: '/hero/recipes/pastrami-on-rye-1024.webp',
    productSlug: 'smallgoods-beef-pastrami-350g-packet',
    pairingSlugs: ['smallgoods-spicy-beef-pastrami-350g-packet', 'smallgoods-smoked-roast-beef-350g-packet'],
    ingredients: [
      '300 g The Meat Works cracked-pepper pastrami, thickly sliced',
      '4 slices rye bread (thickly cut, seeded if possible)',
      '2 tbsp yellow mustard',
      '2 large crisp dill pickles',
      '1 small handful salted potato chips (optional but non-negotiable)',
    ],
    instructions: [
      { name: 'Warm the pastrami', text: 'Steam sliced pastrami over simmering water for 8 – 10 minutes until warmed through and softened. Alternatively, wrap tightly in foil and place in a 160 °C oven for 12 minutes.' },
      { name: 'Toast the rye', text: 'Lightly toast the rye slices — you want a golden crisp shell that still bends without cracking.' },
      { name: 'Build', text: 'Spread each base slice generously with yellow mustard. Pile 150 g pastrami per sandwich, letting it drape over the edges. Top with the second slice.' },
      { name: 'Serve', text: 'Cut on the diagonal, plate with a whole pickle and a small handful of chips. Eat immediately, ideally standing at the kitchen bench.' },
    ],
    tips: [
      'Do NOT add mayo, lettuce, cheese or tomato. Pastrami is loud enough on its own.',
      'For maximum impact, order the pastrami whole and slice yourself, 4 mm thick.',
    ],
    dateISO: '2026-01-28',
  }),

  R('wagyu-biltong-charcuterie', {
    title: 'The Wagyu Biltong Charcuterie Board',
    tagline: 'Ten minutes to build, ninety minutes to graze.',
    description: 'A generous grazing board built around The Meat Works\' house-cured Halal Wagyu biltong, pastrami, salami and jerky. Add pickles, sharp cheddar, honeycomb, mustard and warm sourdough for the entertaining format that never disappoints.',
    cuisine: 'Modern Australian',
    category: 'Entertaining',
    difficulty: 'Easy',
    yield: '6 – 8 grazers',
    prepMinutes: 15,
    cookMinutes: 0,
    keywords: 'charcuterie board halal, wagyu biltong recipe, halal jerky, entertaining board, house cured meats Perth',
    heroImage: '/hero/recipes/wagyu-biltong-charcuterie-1024.webp',
    productSlug: 'jerky-wagyu-biltong-plain-chilli',
    pairingSlugs: ['jerky-biltong-plain-chilli', 'smallgoods-beef-pastrami-350g-packet'],
    ingredients: [
      '200 g The Meat Works Wagyu biltong, sliced',
      '150 g cracked-pepper pastrami, thinly sliced',
      '150 g fennel salami, sliced',
      '100 g original beef jerky, torn',
      '150 g sharp cheddar or aged gouda, wedged',
      'A small block of honeycomb',
      '2 tbsp seeded mustard',
      '1 cup mixed olives',
      '1 cup cornichons',
      '½ cup dried apricots or figs',
      '1 loaf warm sourdough or lavash, torn',
      'Fresh rosemary sprigs, for garnish',
    ],
    instructions: [
      { name: 'Start with the anchors', text: 'Place the mustard bowl, honeycomb and cheeses on opposite ends of the board — these are the visual anchors.' },
      { name: 'Fold the meats', text: 'Rather than laying flat, fold or ribbon each sliced meat into loose piles. Overlap two or three "rivers" of meat between the anchors.' },
      { name: 'Fill the gaps', text: 'Tuck olives, cornichons and dried fruit into every remaining gap. Scatter rosemary sprigs for colour.' },
      { name: 'Serve warm bread on the side', text: 'Warm the sourdough for 8 minutes at 180 °C. Serve alongside the board on a separate wooden board so it doesn\'t steam the meats.' },
    ],
    tips: [
      'The board should look generous — leave no empty white space.',
      'Everything on this board is fully Halal. Perfect for mixed tables.',
    ],
    dateISO: '2026-01-25',
  }),
];

export function findRecipeBySlug(slug) {
  return RECIPES.find((r) => r.slug === slug);
}
